import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth, useWallet } from '@crossmint/client-sdk-react-ui'
import { Wallet } from 'lucide-react'
import { useEtherith } from './hooks/useEtherith'

// Helper function to extract wallet address (same as in useEtherith)
const getWalletAddress = (wallet) => {
  if (!wallet) return null

  const possibleAddressProps = [
    'address', 'walletAddress', 'publicKey', 'account', 'userAddress',
    'smartWallet.address', 'wallet.address', 'account.address'
  ]

  for (const prop of possibleAddressProps) {
    if (prop.includes('.')) {
      const parts = prop.split('.')
      let value = wallet
      for (const part of parts) {
        value = value?.[part]
        if (!value) break
      }
      if (value && typeof value === 'string' && value.startsWith('0x')) {
        return value
      }
    } else {
      const value = wallet[prop]
      if (value && typeof value === 'string' && value.startsWith('0x')) {
        return value
      }
    }
  }
  return null
}
import { ResponsiveProvider, useResponsiveContext } from './context/ResponsiveContext'
import AdaptiveNavigation from './components/AdaptiveNavigation'
import VaultView from './components/VaultView'
import UploadView from './components/UploadView'
import ArchiveView from './components/ArchiveView'
import ProfileView from './components/ProfileView'
import SettingsView from './components/SettingsView'
import DebugPanel from './components/DebugPanel'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import WalletFAB from './components/WalletFAB'
import WalletOnboarding from './components/WalletOnboarding'
import { WalletConnect } from './components/WalletConnect'
import { UIShowcase } from './components/enhanced'
import { commonPatterns } from './utils/mobileFirst'
import ViewportDebugger from './components/ViewportDebugger'

const views = {
  vault: { title: 'Vault', subtitle: 'Preserve what matters', component: VaultView },
  upload: { title: 'Preserve', subtitle: 'Every memory, proofed forever', component: UploadView },
  archive: { title: 'Archive', subtitle: 'Safe. Private. Immutable.', component: ArchiveView },
  profile: { title: 'Identity', subtitle: 'Your digital signature', component: ProfileView },
  settings: { title: 'Protocol', subtitle: 'Configure preservation', component: SettingsView },
  showcase: { title: 'UI Demo', subtitle: 'Enhanced component system', component: UIShowcase }
}

/**
 * AppContent - Main application content (inside ResponsiveProvider)
 */
function AppContent() {
  const [currentView, setCurrentView] = useState('vault')
  const [isArchiveMode, setIsArchiveMode] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const { showSidebar, showBottomNav, shouldAnimate, isMobile } = useResponsiveContext()

  // Crossmint authentication state
  const { jwt, isAuthenticated: crossmintAuth } = useAuth()
  const { wallet } = useWallet()
  const isAuthenticated = !!(jwt && wallet)
  
  // Log authentication state for debugging
  console.log('🔐 Crossmint Auth State:', {
    jwt: !!jwt,
    jwtValue: jwt,
    wallet: !!wallet,
    walletObject: wallet,
    walletAddress: wallet ? getWalletAddress(wallet) : null,
    isAuthenticated,
    crossmintAuth
  })

  const {
    isInitialized,
    user,
    memories,
    publicMemories,
    connectionStatus,
    syncStatus,
    debugInfo,
    uploadMemory,
    loadMemories,
    loadPublicArchive,
    searchArchive,
    checkSyncStatus,
    forcePeerDiscovery,
    peerCount,
    networkStatus,
    isWalletConnected
  } = useEtherith(wallet)

  // Check if onboarding should be shown
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      const hasSeenOnboarding = localStorage.getItem('etherith-onboarding-seen')
      if (!hasSeenOnboarding) {
        // Small delay to let the app initialize
        const timer = setTimeout(() => {
          setShowOnboarding(true)
        }, 1000)
        return () => clearTimeout(timer)
      }
    }
  }, [isInitialized, isAuthenticated])

  // Handle view switching
  const switchView = (viewName) => {
    setCurrentView(viewName)
    setIsArchiveMode(viewName === 'archive')

    // Load view-specific data
    if (viewName === 'archive') {
      loadPublicArchive()
    }
  }

  // Listen for custom view switch events
  useEffect(() => {
    const handleViewSwitch = (event) => {
      switchView(event.detail)
    }

    window.addEventListener('switch-view', handleViewSwitch)
    return () => window.removeEventListener('switch-view', handleViewSwitch)
  }, [loadPublicArchive])

  // Get current view data
  const currentViewData = views[currentView] || views.vault
  const ViewComponent = currentViewData.component

  // Enhanced page transition variants
  const pageVariants = {
    initial: { opacity: 0, y: 30, scale: 0.98 },
    in: { opacity: 1, y: 0, scale: 1 },
    out: { opacity: 0, y: -30, scale: 1.02 }
  }

  const pageTransition = {
    type: 'tween',
    ease: [0.33, 1, 0.68, 1], // luxury easing
    duration: 0.8
  }

  // Show loading only if we have a wallet but haven't initialized yet
  if (wallet?.address && !isInitialized) {
    return (
      <div className="min-h-screen-mobile lg:min-h-screen bg-vault-bg flex items-center justify-center p-content">
        <div className="text-center max-w-sm">
          <motion.div
            className="w-20 h-20 border-4 border-current border-t-transparent rounded-full mx-auto mb-8"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-current mb-3">
            Initializing Etherith...
          </h2>
          <p className="text-base sm:text-lg text-current/70 mb-4">
            Loading memory vault systems
          </p>
          <div className="flex justify-center space-x-1">
            <motion.div
              className="w-2 h-2 bg-current rounded-full"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
            />
            <motion.div
              className="w-2 h-2 bg-current rounded-full"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
            />
            <motion.div
              className="w-2 h-2 bg-current rounded-full"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
            />
          </div>
        </div>
      </div>
    )
  }

  // Wallet connection gate - require authentication for all app functionality
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen-mobile lg:min-h-screen bg-vault-bg flex items-center justify-center p-content">
        <div className="text-center max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-vault-accent to-vault-accent/70 rounded-full mx-auto mb-6 flex items-center justify-center">
              <Wallet className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-current mb-4">
              Connect Your Wallet
            </h1>
            <p className="text-lg text-current/70 mb-8 leading-relaxed">
              Connect your wallet to access your personal memory vault and start preserving your most precious moments.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
            className="space-y-4"
          >
            <WalletConnect
              variant="full"
              showStatus={true}
              className="w-full max-w-sm mx-auto"
            />
            
            <div className="text-sm text-current/50 mt-6">
              <p>Your memories are encrypted and stored securely on IPFS</p>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className={`
      min-h-screen-mobile lg:min-h-screen transition-all duration-slow ease-luxury
      ${isArchiveMode ? 'archive-mode' : 'bg-vault-bg text-vault-text'}
      ${showSidebar ? 'lg:flex lg:flex-row' : ''}
    `}>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      
      {/* Adaptive Navigation System */}
      <AdaptiveNavigation
        currentView={currentView}
        onViewChange={switchView}
        isArchiveMode={isArchiveMode}
        connectionStatus={connectionStatus}
        onSearchPress={() => console.log('Search pressed')}
        onNotificationPress={() => console.log('Notifications pressed')}
        showWalletWidget={true}
      />

      {/* Main Content Layout - Fixed Desktop Layout */}
      <div className={`
        ${showBottomNav ? 'pb-nav-bar' : ''}
        ${showBottomNav ? 'pt-top-bar' : ''}
        ${showSidebar ? 'lg:flex-1 lg:min-h-screen' : ''}
        transition-all duration-500 ease-luxury
        ${!showSidebar ? commonPatterns.responsiveContainer : 'w-full'}
      `}>

        {/* Main Content Area - Fixed Desktop Layout */}
        <main
          id="main-content"
          className={`
            flex flex-col min-h-screen-safe
            ${showSidebar ? 'lg:flex-1 lg:min-h-screen lg:w-full lg:bg-vault-bg' : ''}
            bg-vault-bg transition-all duration-500 ease-luxury
            ${showSidebar ? 'lg:shadow-inner' : ''}
          `}
          role="main"
          aria-label="Main content area"
        >

          {/* Content Container - Fixed Desktop Centering */}
          <div className={`
            flex-1 p-content sm:p-content-sm md:p-content-md lg:p-content-lg xl:p-content-xl
            ${showSidebar ? 'lg:overflow-y-auto lg:h-screen lg:scrollbar-thin lg:w-full' : ''}
            scrollbar-thin relative max-w-full
            ${!showSidebar ? 'lg:max-w-7xl lg:mx-auto xl:max-w-8xl 2xl:max-w-screen-2xl' : ''}
            ${showSidebar ? 'lg:flex lg:flex-col lg:justify-start lg:items-stretch' : ''}
            transition-all duration-500 ease-luxury
          `}>

            {/* Wallet Connection Check */}
            {!isWalletConnected ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                <div className="bg-current/5 rounded-lg p-8 max-w-md">
                  <h2 className="text-xl font-semibold mb-4">Wallet Not Connected</h2>
                  <p className="text-sm opacity-70 mb-6">
                    Please connect your wallet to access the Etherith Memory Vault
                  </p>
                  <div className="text-xs opacity-50">
                    Connect your wallet using the button in the top navigation
                  </div>
                </div>
              </div>
            ) : (
              /* Page Content with Animations */
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentView}
                  initial={shouldAnimate ? "initial" : "in"}
                  animate="in"
                  exit={shouldAnimate ? "out" : "in"}
                  variants={pageVariants}
                  transition={shouldAnimate ? pageTransition : { duration: 0 }}
                  className="h-full"
                >
                <ViewComponent
                  memories={currentView === 'archive' ? publicMemories : memories}
                  user={user}
                  isAuthenticated={isAuthenticated}
                  onUpload={uploadMemory}
                  onSearch={searchArchive}
                  onRefresh={currentView === 'archive' ? loadPublicArchive : loadMemories}
                  isArchiveMode={isArchiveMode}
                />
              </motion.div>
            </AnimatePresence>
            )}

            {/* Debug Panel */}
            <DebugPanel 
              debugInfo={debugInfo} 
              isArchiveMode={isArchiveMode}
              checkSyncStatus={checkSyncStatus}
              forcePeerDiscovery={forcePeerDiscovery}
              peerCount={peerCount}
              networkStatus={networkStatus}
            />
          </div>
        </main>
      </div>

      {/* Mobile Wallet FAB - Provides prominent wallet access */}
      {isMobile && (
        <WalletFAB
          variant="smart"
          position="bottom-right"
          offset="nav-aware"
        />
      )}

      {/* Wallet Onboarding - Auto-shows for new users */}
      <WalletOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        autoShow={true}
      />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Viewport Debugger - Development only */}
      <ViewportDebugger />
    </div>
  )
}

/**
 * App - Main application component with ResponsiveProvider
 */
function App() {
  return (
    <ResponsiveProvider>
      <AppContent />
    </ResponsiveProvider>
  )
}

export default App